import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
//* MUI
import { Box } from '@mui/material';
//* Components
import PrimaryButton from '../../Common/Buttons/PrimaryButton/PrimaryButton';
import ProfileDetailsPopup from '../ProfileDetailsPopup/ProfileDetailsPopup';
import Loader from '../../Common/Loader';
//* Context
import { AppContext } from '../../../AppContext';
//* Enums
import { Actions } from '../../../enums/ActionEnums';
import { Query } from '../../../enums/RequestEnums';
//* Models
import { DepositDetailsModel } from '../../../models/profile';
//* Utils
import { handleError } from '../../../utils/ui';
//* Requests
import {
  GetAccountDetailsQuery,
  StartKycRequest,
  StartKycWithDetailsRequest
} from '../../../queries/profile';
//* Styles
import './AccountDetailsVerifyBox.scss';

const AccountDetailsVerifyBox: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { dispatch } = useContext(AppContext);
  const [openPlayerDetailsPopup, setOpenPlayerDetailsPopup] = useState(false);
  const [playerDetails, setPlayerDetails] = useState<DepositDetailsModel | null>(null);

  const getAccountDetails = useQuery({
    queryKey: Query.GetPlayerDetails,
    queryFn: GetAccountDetailsQuery,
    onSuccess: ({ data }) => {
      setFirstName(data.firstName ?? '');
      setLastName(data.lastName ?? '');
    },
    onError: ({ response }) => handleError(response, dispatch)
  });

  const [firstName, setFirstName] = useState(getAccountDetails.data?.data.firstName || '');
  const [lastName, setLastName] = useState(getAccountDetails.data?.data.lastName || '');

  const showUserDetailsPopUp = firstName === '' || lastName === '';

  const startKycSession = useMutation(StartKycRequest, {
    onSuccess: (data) => {
      if (data.verificationUrl !== null && data.verificationUrl?.length > 0) {
        window.location.href = data.verificationUrl;
      } else if (data.error !== null && data.error.message !== null) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            autoHide: 6000,
            severity: 'error',
            text: data.error.message
          }
        });
      }
    },
    onError: ({ response }) => handleError(response, dispatch)
  });

  const startKycSessionWitDetails = useMutation(StartKycWithDetailsRequest, {
    onSuccess: (data) => {
      if (data.verificationUrl !== null && data.verificationUrl?.length > 0) {
        window.location.href = data.verificationUrl;
      } else if (data.error !== null && data.error.message !== null) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            autoHide: 6000,
            severity: 'error',
            text: data.error.message
          }
        });
      }
    },
    onError: ({ response }) => handleError(response, dispatch)
  });

  const handleSubmit = () => {
    if (showUserDetailsPopUp && playerDetails === null) {
      setPlayerDetails({
        firstName: '',
        lastName: '',
        country: '',
        phoneNumber: '',
        address: '',
        city: '',
        postCode: '',
        currency: ''
      });
      setOpenPlayerDetailsPopup(true);
    } else {
      startKycSession.mutate();
    }
  };

  const handleSubmitDetails = () => {
    startKycSessionWitDetails.mutate(playerDetails as DepositDetailsModel);
  };

  return (
    <Box className="verify-box-container">
      <Box className="verify-box-container__image-wrapper">
        <Box className="verify-box-container__img-box"></Box>
        <Box className="verify-box-container__img"></Box>
      </Box>
      <Box className="verify-box-container__text-box">
        {t('myAccount.verificationPage.description')}
      </Box>
      <Box>
        <PrimaryButton
          onClick={handleSubmit}
          text={t('wallet.withdrawPage.verifyAccountButton') as string}
          className="verify-button"
          disabled={startKycSession.isLoading}
          isLoading={startKycSession.isLoading}
        />
      </Box>
      {openPlayerDetailsPopup && (
        <ProfileDetailsPopup
          open={openPlayerDetailsPopup}
          onClose={() => {
            setOpenPlayerDetailsPopup(false);
            setPlayerDetails(null);
          }}
          playerDetails={playerDetails as DepositDetailsModel}
          setPlayerDetails={setPlayerDetails}
          onSubmit={handleSubmitDetails}
        />
      )}
      <Loader loading={startKycSessionWitDetails.isLoading} />
    </Box>
  );
};

export default AccountDetailsVerifyBox;
